import { useGetList, useGetOne } from 'react-admin';
import { isNativeLineItemId } from 'src/client/utility/pio-native-lineitem-fk-reads';

// The Placements.IO API was shut off at contract end (July 2026), so the legacy
// 'line_items' REST proxy can no longer resolve imported line items — every read
// must come from the native 'lineItems' resource. Imported line items exist there
// too, keyed by externalPioId, so both id forms resolve natively:
//   native UUID        → getOne('lineItems')
//   numeric legacy id  → getList('lineItems', { externalPioId })
// includeArchived keeps archived line items resolvable (the native getOne branch
// has no archived gate, so the legacy-id branch must not silently drop them).
export const useNativeLineItem = (anyLineItemId: string | number | null | undefined) => {
  const id = anyLineItemId === null || anyLineItemId === undefined || anyLineItemId === '' ? undefined : String(anyLineItemId);
  const isNative = isNativeLineItemId(id);

  const one = useGetOne('lineItems', { id: id as string }, { enabled: Boolean(id) && isNative });
  // The explicit equalTo operator matters: ra-postgraphile maps a plain string
  // filter value to the substring `includes` operator, which could match the
  // wrong line item (e.g. '12345' inside '112345').
  const list = useGetList(
    'lineItems',
    {
      filter: { externalPioId: { operator: 'equalTo', value: id }, includeArchived: true },
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'id', order: 'ASC' },
    },
    { enabled: Boolean(id) && !isNative },
  );

  if (!id) {
    return { lineItem: undefined, isLoading: false, error: undefined };
  }

  if (isNative) {
    return { lineItem: one.data, isLoading: one.isLoading, error: one.error ?? undefined };
  }

  const lineItem = list.data?.[0];
  const notFoundError =
    !list.isLoading && !list.error && list.data && !lineItem ? new Error(`Line item not found: ${id}`) : undefined;

  return { lineItem, isLoading: list.isLoading, error: list.error ?? notFoundError };
};
